import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ShoppingCart, Users, Phone, MessageSquare, CalendarDays, ArrowRight,
} from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { LogInteractionModal } from "./LogInteractionModal";

interface QuickActionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuickActionsModal({ open, onOpenChange }: QuickActionsModalProps) {
  const navigate = useNavigate();
  const { jobs } = useAppData();
  const [logOpen, setLogOpen] = useState(false);
  const [logCustomer, setLogCustomer] = useState<string>("");

  const close = () => onOpenChange(false);

  const goto = (path: string) => {
    close();
    navigate(path);
  };

  // Find first customer with a job for "Call / Log" defaults
  const firstCustomer = jobs[0]?.customerName ?? "";

  const actions = [
    {
      label: "Generate PO",
      desc: "Open ordering queue to build a purchase order",
      icon: ShoppingCart,
      onClick: () => goto("/ordering-queue"),
    },
    {
      label: "Assign Crew",
      desc: "Open scheduling & dispatch board",
      icon: Users,
      onClick: () => goto("/scheduling-dispatch"),
    },
    {
      label: "Schedule Appointment",
      desc: "Open appointments calendar",
      icon: CalendarDays,
      onClick: () => goto("/appointments"),
    },
    {
      label: "Call Customer",
      desc: "Open conversations to dial out",
      icon: Phone,
      onClick: () => goto("/messages"),
    },
    {
      label: "Log Interaction",
      desc: "Record a call, text, or note inline",
      icon: MessageSquare,
      onClick: () => {
        setLogCustomer(firstCustomer);
        setLogOpen(true);
        onOpenChange(false);
      },
    },
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Quick Actions</DialogTitle>
            <DialogDescription>
              Execute a high-frequency action without leaving the page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 mt-2">
            {actions.map((a) => (
              <button
                key={a.label}
                onClick={a.onClick}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-accent transition-colors text-left group"
              >
                <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                  <a.icon className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{a.label}</p>
                  <p className="text-xs text-muted-foreground truncate">{a.desc}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
          <div className="flex justify-end pt-2">
            <Button variant="ghost" size="sm" onClick={close}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {logCustomer && (
        <LogInteractionModal
          open={logOpen}
          onOpenChange={setLogOpen}
          customerName={logCustomer}
        />
      )}
    </>
  );
}
